import { useCallback, useEffect, useState } from "react";
import { apiJson } from "../../utils/api.js";

function pill(text) {
  return <div className="pill">{text}</div>;
}

/** Форма Телфин для одного отдела (состояние синхронизируется с props.group при смене id). */
function TelphinGroupForm({ group, onSaved }) {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setClientId(group.telphin_client_id ?? "");
    setClientSecret("");
  }, [group.id, group.telphin_client_id]);

  const save = useCallback(async () => {
    const payload = {
      telphin_client_id: clientId.trim() || null,
    };
    const secretTrim = clientSecret.trim();
    if (secretTrim) {
      payload.telphin_client_secret = secretTrim;
    }

    setBusy(true);
    try {
      await apiJson(`/api/admin/groups/${group.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setClientSecret("");
      await onSaved?.();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [group.id, clientId, clientSecret, onSaved]);

  const clearSecret = useCallback(async () => {
    if (!window.confirm("Удалить сохранённый секрет приложения Телфин для этого отдела?")) return;
    setBusy(true);
    try {
      await apiJson(`/api/admin/groups/${group.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telphin_client_secret: null }),
      });
      setClientSecret("");
      await onSaved?.();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [group.id, onSaved]);

  return (
    <div className="telephonyGroupForm">
      <div className="telephonyFieldRow">
        <label className="telephonyLabel" htmlFor={`tph-cid-${group.id}`}>
          Client ID (приложение Телфин)
        </label>
        <input
          id={`tph-cid-${group.id}`}
          type="text"
          className="input telephonyFieldInput"
          autoComplete="off"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          placeholder="из кабинета Телфин"
        />
      </div>
      <div className="telephonyFieldRow">
        <label className="telephonyLabel" htmlFor={`tph-sec-${group.id}`}>
          Client Secret
        </label>
        <div className="telephonySecretLine">
          <input
            id={`tph-sec-${group.id}`}
            type="password"
            className="input telephonyFieldInput"
            autoComplete="new-password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={
              group.telphin_has_client_secret ? "Пусто — не менять; новый — заменить" : "секрет приложения"
            }
          />
          {group.telphin_has_client_secret ? (
            <button
              type="button"
              className="telephonySecretDeleteBtn"
              onClick={clearSecret}
              disabled={busy}
              title="Удалить секрет"
              aria-label="Удалить сохранённый секрет приложения Телфин"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                <line x1="10" x2="10" y1="11" y2="17" />
                <line x1="14" x2="14" y1="11" y2="17" />
              </svg>
            </button>
          ) : null}
        </div>
      </div>
      <div className="telephonyFormActions">
        <button type="button" className="smallBtn" disabled={busy} onClick={save}>
          {busy ? "…" : "Сохранить отдел"}
        </button>
      </div>
    </div>
  );
}

function ManagerExtensionRow({ manager, onSaved }) {
  const [ext, setExt] = useState(manager.telphin_extension_id ?? "");
  const [srcNum, setSrcNum] = useState(manager.telphin_src_num ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setExt(manager.telphin_extension_id ?? "");
    setSrcNum(manager.telphin_src_num ?? "");
  }, [manager.id, manager.telphin_extension_id, manager.telphin_src_num]);

  const save = useCallback(async () => {
    setBusy(true);
    try {
      await apiJson(`/api/admin/managers/${manager.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          telphin_extension_id: ext.trim() ? ext.trim() : null,
          telphin_src_num: srcNum.trim() ? srcNum.trim() : null,
        }),
      });
      await onSaved?.();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [manager.id, ext, srcNum, onSaved]);

  const name = manager.display_name || manager.code || `id ${manager.id}`;
  const dept = (manager.group_titles || []).join(", ") || "—";

  return (
    <div className="telephonyManagerBlock">
      <div className="telephonyManagerTop">
        <span className="telephonyManagerName">{name}</span>
        <span className="telephonyManagerSep" aria-hidden="true">
          ·
        </span>
        <span className="telephonyManagerDept" title={dept}>
          {dept}
        </span>
      </div>
      <div className="telephonyManagerControls">
        <input
          type="text"
          className="input telephonyExtInput"
          value={ext}
          onChange={(e) => setExt(e.target.value)}
          placeholder="CallerExtensionID, напр. 1077023"
          title="ID добавочной из API Телфин (CallerExtensionID в вебхуке)"
          disabled={busy}
        />
        <input
          type="text"
          className="input telephonyExtInput telephonySrcInput"
          value={srcNum}
          onChange={(e) => setSrcNum(e.target.value)}
          placeholder="Исходящая линия (src_num)"
          title="Номер линии АТС для callback; если пусто — из настроек отдела"
          disabled={busy}
        />
        <button type="button" className="smallBtn telephonyManagerOkBtn" disabled={busy} onClick={save}>
          {busy ? "…" : "OK"}
        </button>
      </div>
    </div>
  );
}

export function TelephonySettingsSection({ groups, managers, loading, error, onReload }) {
  const webhookUrl =
    typeof window !== "undefined" && window.location?.origin
      ? `${window.location.origin.replace(/\/$/, "")}/webhooks/telphin`
      : "/webhooks/telphin";

  return (
    <>
      <div className="settingsBlock">
        <div className="sectionTitleRow">
          <span className="sectionTitle">Телфин: вебхук</span>
        </div>
        <p className="crmMeta telephonyHint">
          Укажите в кабинете Телфин URL для событий (после регистрации подписки):{" "}
          <code className="telephonyCode">{webhookUrl}</code>
        </p>
      </div>

      <div className="settingsBlock">
        <div className="sectionTitleRow">
          <span className="sectionTitle">Отделы (Телфин API)</span>
        </div>
        {error ? pill(`Ошибка: ${error}`) : null}
        {!loading && !error && (!groups || !groups.length) ? pill("Нет отделов. Создайте отдел во вкладке «Команда».") : null}
        {!loading &&
          !error &&
          (groups || []).map((g) => (
            <div className="telephonyGroupCard" key={String(g.id)}>
              <div className="telephonyGroupTitle">{g.title}</div>
              <TelphinGroupForm group={g} onSaved={onReload} />
            </div>
          ))}
      </div>

      <div className="settingsBlock">
        <div className="sectionTitleRow">
          <span className="sectionTitle">Менеджеры (добавочная и линия)</span>
        </div>
        <div id="telephonyManagersList" style={{ marginTop: 12 }}>
          {!loading && !error && (!managers || !managers.length) ? pill("Нет менеджеров") : null}
          {!loading &&
            !error &&
            (managers || []).map((m) => (
              <ManagerExtensionRow key={String(m.id)} manager={m} onSaved={onReload} />
            ))}
        </div>
      </div>
    </>
  );
}
